import { getDocumentLabel } from '@/constants/booking-details.constants';
import { detectFileType, openPdfInNewTab } from '@/utils/fileConversion';
import { Download, Eye, FileText } from 'lucide-react';
import React from 'react';
import { SECTION_CONFIGS } from '../../constants/sectionConfig.constants';
import type { Application, ApplicationDocument } from '../../types/application.types';
import { CollapsibleSection } from '../shared/CollapsibleSection';

interface DocumentsStepProps {
  application: Application;
}

const formatFileSize = (bytes: number | undefined): string => {
  if (!bytes) return 'N/A';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const DocumentsStep: React.FC<DocumentsStepProps> = ({ application }) => {
  const documents = application.documents || [];
  const config = SECTION_CONFIGS.documents;

  const handleView = (doc: ApplicationDocument) => {
    const fileType = detectFileType(doc.fileName, doc.mimeType);

    if (fileType === 'pdf') {
      openPdfInNewTab({
        url: doc.fileUrl,
        type: doc.mimeType,
        name: doc.fileName,
      });
    } else if (fileType === 'image') {
      window.open(doc.fileUrl, '_blank');
    } else {
      handleDownload(doc);
    }
  };

  const handleDownload = (doc: ApplicationDocument) => {
    const link = document.createElement('a');
    link.href = doc.fileUrl;
    link.download = doc.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (documents.length === 0) {
    return (
      <CollapsibleSection title={config.title} color={config.color} icon={config.icon} defaultOpen={config.defaultOpen}>
        <div className="text-center py-8">
          <FileText size={48} className="mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500">Aucun document fourni</p>
        </div>
      </CollapsibleSection>
    );
  }

  return (
    <CollapsibleSection title={config.title} color={config.color} icon={config.icon} defaultOpen={config.defaultOpen}>
      <div className="space-y-3">
        {documents.map((doc) => {
          const label = getDocumentLabel(doc.documentKey, application.documentLabels);
          const fileType = detectFileType(doc.fileName, doc.mimeType);

          return (
            <div
              key={doc.id}
              className="bg-slate-50 border border-slate-200 rounded-xl p-4 hover:bg-slate-100 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h4 className="text-base font-semibold text-slate-800 mb-1">{label}</h4>
                  <p className="text-sm text-slate-600 mb-2 truncate">{doc.fileName}</p>
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium border"
                      style={{
                        backgroundColor: `${config.color}15`,
                        borderColor: `${config.color}30`,
                        color: config.color,
                      }}
                    >
                      {fileType.toUpperCase()}
                    </span>
                    {doc.fileSize && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-slate-200 text-slate-700">
                        {formatFileSize(doc.fileSize)}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleView(doc)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-colors hover:bg-white"
                    style={{
                      borderColor: `${config.color}30`,
                      color: config.color,
                    }}
                  >
                    <Eye size={16} />
                    <span>Voir</span>
                  </button>
                  <button
                    onClick={() => handleDownload(doc)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-slate-300 text-slate-700 transition-colors hover:bg-white"
                  >
                    <Download size={16} />
                    <span>Télécharger</span>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </CollapsibleSection>
  );
};
